import { API_TOKEN_COOKIE_NAME, ASPNET_APPLICATION_COOKIE_NAME } from '@/constants/cookies.js'
import { READ_ONLY_TOKEN_SESSION_STORAGE_KEY } from '@/constants/sessionStorage.js'
import { USER_LOCAL_STORAGE_KEY } from '@/constants/localStorage.js'
import { PROFESSIONAL_ROLE, PATIENT_ROLE, CENTER_ROLE, API_CLIENT_ROLE } from '@/constants/roles.js'
import {EDUCATIVE_CENTER_CODE_TYPE} from '@/constants/db/companyCodeTypes.js'
import { getCookie, setCookie, removeCookie } from '@/helpers/cookies.helpers'
import AccountRepository from '@/repositories/AccountRepository'
import Vue from 'vue'
import { HEALTHY_COMMUNITY_SERVICE, SEARCH_SERVICE } from '@/constants/patientServices'
import * as GENDERS from '@/constants/genders'

function getUserFromLocalStorage () {
	const user = localStorage.getItem(USER_LOCAL_STORAGE_KEY)
	if (user) {
		try {
			return JSON.parse(user)
		} catch (e) {
			console.error('Error parsing user from localStorage:', e)
			localStorage.removeItem(USER_LOCAL_STORAGE_KEY)
			return null
		}
	}
	return null
}

function setUserInLocalStorage (user) {
	if (user) {
		localStorage.setItem(USER_LOCAL_STORAGE_KEY, JSON.stringify(user))
	}
}

function removeUserFromLocalStorage () {
	localStorage.removeItem(USER_LOCAL_STORAGE_KEY)
}

const sessionStore = {
	namespaced: true,
	state: {
		apiToken: null,
		loginInfo: null,
		user: null,
		requiredLoginFields: [],
		loginOptionalActions: [],
		loginCallback: null,
		changeUserModalVisible: false,
		sessionHost: null,
	},
	mutations: {
		setLoginInfo (state, loginInfo) {
			state.loginInfo = loginInfo
		},
		setLogged (state, token) {
			setCookie(API_TOKEN_COOKIE_NAME, token, 1)
			Vue.prototype.$apiToken = token
			state.apiToken = token
		},
		setUser (state, user) {
			state.user = user
			setUserInLocalStorage(user)
		},
		setUserAge (state, age) {
			state.user.age = age
		},
		setApiToken (state, token) {
			setCookie(API_TOKEN_COOKIE_NAME, token, 1)
			Vue.prototype.$apiToken = token
			state.apiToken = token
		},
		setUserCookies (state, cookies) {
			if (!cookies) { return }
			const cookiesList = Object.entries(cookies)
			if (cookiesList && cookiesList.length > 0) {
				cookiesList.forEach(entry => {
					const [key, value] = entry
					setCookie(key, value, 1)
				})
			}
		},
		setRequiredLoginFields (state, data) {
			state.requiredLoginFields = data
		},
		setLoginOptionalActions (state, data) {
			state.loginOptionalActions = data
		},
		removeLoginOptionalAction(state, action) {
			state.loginOptionalActions = state.loginOptionalActions.filter(a => a !== action)
		},
		setLoginCallback(state, data) { 
			state.loginCallback = data
		},
		setSessionHost (state) {
			state.sessionHost = window.location.host
		}
	},
	getters: {
		companyCodeInfoMessages: state => (state.user && state.user.companyCode && state.user.companyCode.infoMessages) ? state.user.companyCode.infoMessages : [],
		userIsType: (state) => (userType) => state.user && state.user.userType === userType,
		userIsProfessional: (state, getters) => getters.userIsType(PROFESSIONAL_ROLE),
		userIsPatient: (state, getters) => getters.userIsType(PATIENT_ROLE),
		userIsCenter: (state, getters) => getters.userIsType(CENTER_ROLE),
		userIsApiClient: (state, getters) => getters.userIsType(API_CLIENT_ROLE),
		userHasCompanies: (state) => state.user && state.user.companiesActive,
		userIsCenterEducative: (state) => state.user.companyCode.codeType === EDUCATIVE_CENTER_CODE_TYPE,
		isSessionRoleAllowed: (state) => (allowedRoles) => state.user && allowedRoles.includes(state.user.userType),
		getCompanyService: (state) => (service) => state.user && state.user.activeCompanyServices ? state.user.activeCompanyServices.find(s => s.id === service) : null,
		userHasActiveCompanyService: (state, getters) => (service) => (getters.getCompanyService(service) && getters.getCompanyService(service).active),
		getHealthyCommunityService (state, getters) {
			return getters.getCompanyService(HEALTHY_COMMUNITY_SERVICE)
		},
		healthyCommunityActive (state, getters) {
			return getters.userHasActiveCompanyService(HEALTHY_COMMUNITY_SERVICE) || getters.userHasPaidSomething || getters.userIsProfessional
		},
		search360Active (state, getters) {
			return getters.userHasActiveCompanyService(SEARCH_SERVICE)
		},
		userIsMale (state) {
			return state.user && state.user.sex === GENDERS.MALE
		},
		userIsFemale (state) {
			return state.user && state.user.sex === GENDERS.FEMALE
		},
		referralActiveByApiClient (state, getters) {
			return getters.userIsApiClient && !!state.user.companyCode
		},
		menstruationsActive (state, getters) {
			return getters.userIsFemale && state.user.menstruations && state.user.menstruations.length > 0
		},
		userHasPaidSomething (state) {
			return state.user && state.user.hasPaidSomething
		},
		isLogged (state) {
			return state.apiToken || getCookie(API_TOKEN_COOKIE_NAME) || sessionStorage.getItem(READ_ONLY_TOKEN_SESSION_STORAGE_KEY)
		},
		isLoggedInHost (state) {
			return state.apiToken && state.sessionHost === window.location.host
		},
		getUserFromLocalStorage: () => {
			return getUserFromLocalStorage()
		}
	},
	actions: {
		set ({ commit }, loginInfo) {
			commit('setLoginInfo', loginInfo)
			commit('setUser', loginInfo.user)
			commit('setUserCookies', loginInfo.cookies)
			commit('setLoginOptionalActions', loginInfo.optionalActions)
			commit('setLogged', loginInfo.apiToken)
			commit('setSessionHost')
		},
		async create ({ state, commit, dispatch }, payload = { token: null, loginInfo: null, reload: true }) {
			if (state.user && state.apiToken && state.apiToken === window.$cookies.get(API_TOKEN_COOKIE_NAME)) {
				return
			}
			try {
				dispatch('remove')
			} catch (e) {
				// do nothing
			}
			if (payload.token) {
				removeCookie(API_TOKEN_COOKIE_NAME)
				commit('setApiToken', payload.token)
			}
			if ((!state.user || !state.apiToken)) {
				try {
					const loginInfo = payload.loginInfo || (await AccountRepository.getSession().catch(e => {
						return null
					})).data
					dispatch('set', loginInfo)
					if (payload.reload) { window.location.reload() }
					return loginInfo
				} catch (e) {
					return null
				}
			}
		},
		remove ({ state, dispatch }) {
			state.user = null
			removeUserFromLocalStorage()
			state.apiToken = null
			state.sessionHost = null
			removeCookie(API_TOKEN_COOKIE_NAME)
			removeCookie(ASPNET_APPLICATION_COOKIE_NAME)
			dispatch('notifications/resetState', null, { root: true })
		}
	}
}

export default sessionStore


// WEBPACK FOOTER //
// ./src/store/modules/session.js